import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { Hub } from "aws-amplify/utils";
import {
  fetchAuthSession,
  signInWithRedirect,
  confirmSignIn,
} from "aws-amplify/auth";
import { setUserData, getUserData } from "../../Actions/UserDataActions";
import { loginUser, confirmMFA } from "../../Services/Auth/AuthAPI";
import { MESSAGES } from "../../Utils/ErrorMessages";
import { useNavigate } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";

import Container from "../../Layouts/Common/Container/Container";
import {
  AuthenticatedBuyerUserHomepage,
  AuthenticatedSupplierUserHomepage,
  SupplierCognitoGroup,
  FederatedIDPProvider,
} from "../../Utils/Constants";
import { doesUserHasResourceAccess } from "../../Utils/HelperFunctions";

const Login = (props) => {
  const navigate = useNavigate();

  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [showOtp, setShowOtp] = useState(false);

  const [user, setUser] = useState({
    username: "",
    password: "",
  });

  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");

  const [error, setError] = useState({});

  useEffect(() => {
    localStorage.clear();
  }, []);

  // ✅ FIXED HUB (SSO)
  useEffect(() => {
    const unsubscribe = Hub.listen("auth", async ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
          try {
            const session = await fetchAuthSession();

            const idTokenPayload = session.tokens?.idToken?.payload;
            const accessTokenPayload = session.tokens?.accessToken?.payload;

            const payloadData = {
              ...accessTokenPayload,
              ...idTokenPayload,
              "cognito:groups":
                idTokenPayload?.["cognito:groups"] ??
                accessTokenPayload?.["cognito:groups"] ??
                [],
            };

            localStorage.setItem("cognitoUser", "true");
            localStorage.setItem("isAuthenticated", "true");
            localStorage.setItem("userData", JSON.stringify(payloadData));

            props.setUserData({ ...payloadData, isAuthenticated: true });

            if (doesUserHasResourceAccess(SupplierCognitoGroup)) {
              navigate(AuthenticatedSupplierUserHomepage);
            } else {
              navigate(AuthenticatedBuyerUserHomepage);
            }
          } catch (err) {
            console.error(err);
          }
          break;

        case "signInWithRedirect_failure":
          console.error("SSO failed");
          break;

        default:
          break;
      }
    });

    return () => unsubscribe();
  }, [navigate, props]);

  const hydrateAuthenticatedUser = useCallback(async () => {
    const session = await fetchAuthSession();

    const idTokenPayload = session.tokens?.idToken?.payload ?? {};
    const accessTokenPayload = session.tokens?.accessToken?.payload ?? {};

    const payload = {
      ...accessTokenPayload,
      ...idTokenPayload,
      "cognito:groups":
        idTokenPayload["cognito:groups"] ??
        accessTokenPayload["cognito:groups"] ??
        [],
    };

    localStorage.setItem("cognitoUser", "true");
    localStorage.setItem("isAuthenticated", "true");
    localStorage.setItem("userData", JSON.stringify(payload));

    props.setUserData({ ...payload, isAuthenticated: true });

    if (doesUserHasResourceAccess(SupplierCognitoGroup)) {
      navigate(AuthenticatedSupplierUserHomepage);
    } else {
      navigate(AuthenticatedBuyerUserHomepage);
    }
  }, [navigate, props]);

  // LOGIN
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateUser()) return;

    try {
      setOtpLoading(true);

      const response = await loginUser(user.username, user.password);

      const step = response?.nextStep?.signInStep;

      if (step === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
        setShowOtp(true);
        return;
      }

      if (response?.isSignedIn || step === "DONE") {
        await hydrateAuthenticatedUser();
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setOtpLoading(false);
    }
  };

  //  OTP
  const verifyOtp = async () => {
    try {
      const res = await confirmSignIn({ challengeResponse: otp });

      if (res.isSignedIn || res.nextStep?.signInStep === "DONE") {
        setShowOtp(false);
        await hydrateAuthenticatedUser();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  //  MFA
  const handleConfirmMFA = async () => {
    try {
      const result = await confirmMFA(mfaCode);
      if (result.success) await hydrateAuthenticatedUser();
    } catch (err) {
      alert(err.message);
    }
  };

  //  SSO
  const FederatedLogin = async () => {
    try {
      localStorage.setItem("idpLogin", "true");
      await signInWithRedirect({ provider: FederatedIDPProvider });
    } catch (err) {
      console.error(err);
    }
  };

  const onHandleChange = (e, name) => {
    setUser({ ...user, [name]: e.target.value });
    setError({});
  };

  const validateUser = () => {
    if (!user.username || !user.password) return false;
    return true;
  };

  return (
    <Container pageName={"Login"}>
      <div className="row">
        <div className="col-md-5">
          <p className="Suppliersheading">Suppliers</p>

          {otpLoading ? (
            <div className="loginForm emailOtpLoadder">
              <ClipLoader
                color={"#008080"}
                size={40}
                aria-label="Loading Spinner"
                data-testid="loader"
              />
              <div style={{ marginTop: "10px", fontSize: "14px" }}>
                Sending verification code...
              </div>
            </div>
          ) : !showOtp ? (
            <form className="loginForm">
              <div className="form-group loginLabel">
                <div className="required emailLabel">Email</div>
                <input
                  type="email"
                  className="form-control w-100"
                  onChange={(e) => onHandleChange(e, "username")}
                  required
                />
              </div>

              <div className="form-group loginLabel">
                <div className="required">Password</div>
                <input
                  type="password"
                  className="form-control w-100"
                  onChange={(e) => onHandleChange(e, "password")}
                  required
                />
              </div>

              <p>
                <a href="forgot-password" className="forgetPasswordLink">
                  Forgot password ?
                </a>
              </p>

              <div>
                <button
                  type="button"
                  className="Loginbutton"
                  onClick={handleSubmit}
                  disabled={otpLoading}
                >
                  Login
                </button>

                <p className="signUpTab">
                  <span>Not a member?</span>{" "}
                  <span>
                    <a href="/signup" className="SignUpLink">
                      Sign-Up
                    </a>
                  </span>
                </p>
              </div>
            </form>
          ) : (
            <form className="loginForm">
              <div className="emailVerificationHeading">
                Email Verification
              </div>

              <div className="emailVerificationHelpText">
                Please check your email for the verification code and enter it
                below to continue.
              </div>

              <div className="form-group loginLabel">
                <input
                  type="text"
                  className="form-control w-100"
                  placeholder="Enter verification code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  autoFocus
                  required
                />
              </div>

              <button
                type="button"
                className="Loginbutton"
                onClick={verifyOtp}
              >
                Verify
              </button>

              <button
                type="button"
                className="Loginbutton"
                style={{
                  background: "#ccc",
                  color: "#333",
                  marginTop: "10px",
                }}
                onClick={() => {
                  setShowOtp(false);
                  setOtp("");
                }}
              >
                Cancel
              </button>
            </form>
          )}
        </div>

        <div className="col-md-2">
          <div className="vertical"></div>
        </div>

        <div className="col-md-5">
          <p className="Buyersheading">Buyers</p>

          <div className="intro">
            <p>
              Please use your organisation account to access Williams lea
              Procurement portal
            </p>
            <p>
              To change or reset your organisation password, please contact your
              system administrator or helpdesk
            </p>
          </div>

          <div className="loginButton">
            <button
              type="button"
              onClick={FederatedLogin}
              className="buyerLoginbutton"
            >
              Login via Organisation
            </button>
          </div>
        </div>
      </div>

      {mfaRequired && (
        <div className="mfaSection">
          <label>Email Verification Code</label>

          <input
            type="text"
            className="form-control"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
          />

          <button className="Loginbutton" onClick={handleConfirmMFA}>
            Verify Code
          </button>
        </div>
      )}
    </Container>
  );
};

export default connect(null, {
  setUserData,
  getUserData,
})(Login);